/**
 * Mobile "Mehr" overflow sheet (PHASE1 §7.1). Lists the nav items that do not
 * fit the bottom bar, plus the current user and logout. Navigating closes it.
 */
import { NavLink } from 'react-router';
import { LogOut, Paperclip } from 'lucide-react';
import { Sheet } from '../ui/Sheet.tsx';
import type { NavItem } from './AppShell.tsx';
import { useCurrentUser, useLogout } from '../../lib/hooks/useCurrentUser.ts';
import { useTheme } from '../../context/ThemeContext.tsx';

// "Belege" isn't part of `AppShell.NAV_ITEMS`, so it never reaches the
// overflow `items` this sheet receives from `BottomNav`; it's spliced in here
// (mirrors the same insertion in `Sidebar.tsx`, next to Import since
// Transaktionen lives in the primary bottom bar, not this sheet).
const BELEGE_NAV_ITEM: NavItem = { to: '/belege', label: 'Belege', icon: Paperclip };

function withBelege(items: NavItem[]): NavItem[] {
  const index = items.findIndex((item) => item.to === '/import');
  if (index === -1) return [BELEGE_NAV_ITEM, ...items];
  return [...items.slice(0, index + 1), BELEGE_NAV_ITEM, ...items.slice(index + 1)];
}

/** Inline sun/moon glyphs for the theme toggle (no icon-CDN dependency). */
function SunIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5 shrink-0"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5 shrink-0"
      aria-hidden
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
    </svg>
  );
}

export interface MoreSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: NavItem[];
}

export function MoreSheet({ open, onOpenChange, items }: MoreSheetProps) {
  const user = useCurrentUser();
  const { logout, isLoggingOut } = useLogout();
  const { theme, toggleTheme } = useTheme();
  const themeLabel = theme === 'dark' ? 'Zum hellen Modus wechseln' : 'Zum dunklen Modus wechseln';

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Mehr">
      <div className="space-y-1">
        {withBelege(items).map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => onOpenChange(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-100'
              }`
            }
          >
            <Icon className="h-5 w-5 shrink-0" />
            {label}
          </NavLink>
        ))}
      </div>

      <div className="mt-2 border-t border-slate-100 px-3 pt-3 dark:border-slate-800/70">
        <p className="truncate text-xs text-slate-500 dark:text-slate-400" title={user.email}>
          {user.display_name ?? user.email}
        </p>
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={themeLabel}
          title={themeLabel}
          className="mt-2 flex w-full items-center gap-3 rounded-lg px-0 py-2 text-sm font-medium text-slate-600 transition-colors duration-150 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          {theme === 'dark' ? 'Heller Modus' : 'Dunkler Modus'}
        </button>
        <button
          type="button"
          onClick={() => void logout()}
          disabled={isLoggingOut}
          className="mt-1 flex w-full items-center gap-3 rounded-lg px-0 py-2 text-sm font-medium text-slate-600 transition-colors duration-150 hover:text-slate-900 disabled:opacity-60 dark:text-slate-400 dark:hover:text-slate-100"
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {isLoggingOut ? 'Wird abgemeldet …' : 'Abmelden'}
        </button>
      </div>
    </Sheet>
  );
}
